class IndexCarousel extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.init === 'false') {
      return;
    }

    this.header = document.querySelector('.site-header');
    this.duration = parseInt(this.dataset.duration, 10) || 10000;

    this.carousel = document.querySelector('index-carousel');
    this.autoplay = this.carousel.dataset.autoplay;

    this.container = this.querySelector('[container]');
    this.slides = [...this.container.querySelectorAll('[slide]')];
    this.totalSlides = this.slides.length;

    this.carouselTitle = this.querySelector('[data-carousel-title-element]');
    this.currentSlide = 0;
    this.progressBarTimer = null;

    this.carouselActions = this.querySelector('[data-carousel-actions]');
    this.buttonPrevious = this.querySelector('[previous]');
    this.buttonNext = this.querySelector('[next]');

    if (!this.container || this.totalSlides == 0) {
      console.error('Container or slides not found.');
      return;
    }

    if (this.totalSlides === 0) {
      console.error('No slides found.');
      return;
    }

    this.currentIndex = 1;
    this.timer = null;
    this.touchStartX = null;

    this.progressBar = this.querySelector('[progress-bar]');

    this.cloneFirstAndLastSlides();

    // Remove lazy loading from next slide and initialize slideshow
    this.removeLazyLoadFromNextSlide(this.currentIndex);
    this.initLabel();
    this.updateHeaderColor();

    if (this.autoplay === 'true') {
      this.initSlideShow();
      this.updateProgressBar();
    }

    // Add scroll event listeners with debounced handler
    this.container.addEventListener(
      'scroll',
      debounce(() => this.handleScrollOrSlideChange(), 100),
    );

    // Add touch event listeners for swipe navigation
    this.addEventListener('touchstart', (event) => this.handleTouchStart(event));
    this.addEventListener('touchmove', (event) => this.handleTouchMove(event));
    window.addEventListener('resize', (event) => this.handleResize(event));
    // Add navigation button event handlers after DOM is fully loaded
    this.addNavigationEventHandlers();

    this.togglePlayPauseVideo();
  }

  togglePlayPauseVideo() {
    const pauseButtons = this.querySelectorAll('[data-play-pause-button]');

    pauseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const isAutoplayActive = this.autoplay === 'true';
        const allVideos = this.querySelectorAll('video');

        if (isAutoplayActive) {
          this.autoplay = 'false';
          clearInterval(this.timer);
          this.timer = null;
          this.resetProgressBar();
          this.changeIcon(button, 'play');
          allVideos.forEach((video) => {
            video.pause();
          });
        } else {
          this.autoplay = 'true';
          this.initSlideShow();
          this.updateProgressBar();
          this.changeIcon(button, 'pause');
          allVideos.forEach((video) => {
            video.play();
          });
        }
      });
    });
  }

  changeIcon(button, action) {
    if (action === 'play') {
      const ariaLabel = button.dataset.ariaLabelPlay || 'Play slideshow';
      button.setAttribute('aria-label', ariaLabel);
      button.querySelector('.icon-play').classList.remove('hidden');
      button.querySelector('.icon-pause').classList.add('hidden');
    } else if (action === 'pause') {
      const ariaLabel = button.dataset.ariaLabelPause || 'Pause slideshow';
      button.setAttribute('aria-label', ariaLabel);
      button.querySelector('.icon-play').classList.add('hidden');
      button.querySelector('.icon-pause').classList.remove('hidden');
    }
  }

  initLabel() {
    const slide = this.slides[0];
    const carouselTitle = slide.dataset.carouselTitle;
    if (carouselTitle) {
      const ruler = this.querySelector('[data-carousel-ruler]');
      ruler.innerHTML = carouselTitle;
      requestAnimationFrame(() => {
        this.carouselTitle.style.width = `${ruler.clientWidth + 1}px`;
        requestAnimationFrame(() => {
          this.carouselTitle.style.transitionProperty = 'width, opacity';
          this.carouselActions
            .querySelectorAll('button')
            .forEach((x) => x.classList.replace('opacity-0', 'opacity-100'));
        });
      });
    }
  }

  initSlideShow() {
    this.handleA11yUpdates();
    if (this.dataset.duration && !isNaN(this.dataset.duration) && parseInt(this.dataset.duration, 10) > 0) {
      this.duration = parseInt(this.dataset.duration, 10);

      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.currentIndex = (this.currentIndex + 1) % (this.totalSlides + 2);
        this.scrollToSlide();
        this.updateProgressBar();
      }, this.duration);
    }
  }

  handleA11yUpdates() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const activeSlide = entry.target;
            const inactiveSlides = [...this.querySelectorAll('[slide]')].filter((slide) => slide !== activeSlide);

            activeSlide.setAttribute('aria-hidden', false);
            activeSlide.querySelectorAll('[tabindex]').forEach((focusItem) => focusItem.removeAttribute('tabindex'));

            inactiveSlides.forEach((inactiveSlide) => {
              inactiveSlide.setAttribute('aria-hidden', true);
              inactiveSlide
                .querySelectorAll('input, button, a, select, textarea')
                .forEach((focusItem) => focusItem.setAttribute('tabindex', '-1'));
            });
          }
        });
      },
      {
        threshold: 0.51,
      },
    );
    this.querySelectorAll('[slide]').forEach((slide, index) => {
      this.observer.observe(slide);
    });
  }

  handleScrollOrSlideChange() {
    this.pauseAllVideos();
  }

  pauseAllVideos() {
    this.slides.forEach((slide, index) => {
      const currentIndexMod = (this.currentIndex - 1 + this.totalSlides + 1) % (this.totalSlides + 1);

      if (index !== currentIndexMod) {
        const video = slide.querySelector('video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }

  playVideo(slide) {
    if (!slide) {
      return;
    }
    const video = slide.querySelector('video');
    if (video) {
      video.play();
    }
  }

  scrollToSlide() {
    // Reset the progress bar before moving to the next slide
    this.resetProgressBar();

    const currentIndexMod = (this.currentIndex - 1) % this.totalSlides;
    this.removeLazyLoadFromNextSlide(currentIndexMod);

    setTimeout(() => {
      // Ensure the progress bar is reset even if no video is present
      this.resetProgressBar(); // <-- Call reset again after transition to new slide.

      // Handle video playback
      if (this.isElementInViewport(this)) {
        this.playVideo(this.slides[currentIndexMod]);

        if (currentIndexMod == 0) {
          this.playVideo(this.slides[0]);
        }
      }

      // Scroll logic
      const left = this.currentIndex * this.slides[0].clientWidth;
      const leftFirstSlide = this.slides[0].clientWidth;
      const leftLastSlide = this.totalSlides * this.slides[0].clientWidth;
      const speed = 300;

      // Handle slide scroll and looping
      if (this.currentIndex === this.totalSlides + 1) {
        window.smoothScrollTo(this.container, left, speed, 'scrollLeft');
        this.handleScrollEnd(left).then(() => {
          this.currentIndex = 1;
          window.smoothScrollTo(this.container, leftFirstSlide, 0, 'scrollLeft');
        });
      } else if (this.currentIndex === 0) {
        window.smoothScrollTo(this.container, left, speed, 'scrollLeft');
        this.handleScrollEnd(left).then(() => {
          this.currentIndex = this.totalSlides;
          window.smoothScrollTo(this.container, leftLastSlide, 0, 'scrollLeft');
        });
      } else {
        window.smoothScrollTo(this.container, left, speed, 'scrollLeft');
      }

      // Final updates after transition
      this.animateSlideTitle();
      this.updateHeaderColor();
      this.updateProgressBar(); // Ensure the progress bar is updated
    }, 300);
  }

  // Improved resetProgressBar method
  resetProgressBar() {
    // Clear the progress bar animation timer
    if (this.progressBarTimer) {
      cancelAnimationFrame(this.progressBarTimer);
      clearInterval(this.progressBarTimer);
      this.progressBarTimer = null;
    }

    // Reset the width immediately (important if transitioning to non-video slide)
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
    }
  }

  updateProgressBar() {
    if (this.dataset.duration && !isNaN(this.dataset.duration) && parseInt(this.dataset.duration, 10) > 0) {
      const increment = 100 / this.duration;
      let progressWidth = 0;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsedTime = currentTime - startTime;
        progressWidth = Math.min(increment * elapsedTime, 100);

        this.progressBar.style.width = `${progressWidth}%`;

        if (progressWidth < 100) {
          // Store the reference to the current animation frame
          this.progressBarTimer = requestAnimationFrame(animate);
        }
      };

      // Start the animation
      this.progressBarTimer = requestAnimationFrame(animate);
    }
  }

  updateHeaderColor() {
    let currentIndexMod = (this.currentIndex - 1) % this.totalSlides;
    if (currentIndexMod < 0) {
      currentIndexMod = this.totalSlides - 1;
    } else if (currentIndexMod > this.totalSlides) {
      currentIndexMod = 0;
    }
    const slide = this.slides[currentIndexMod];
    const color = slide ? slide.dataset.headerColor || 'black' : 'black';

    this.style.setProperty('--text-color', color);
    this.setAttribute('data-header-color', color);

    if (this.isElementInViewport(this)) {
      this.header.style.color = color;
    }
  }

  // Check if more than 50% of an element is in the viewport
  isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    const elementHeight = rect.height;
    const elementWidth = rect.width;

    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleArea = visibleHeight * visibleWidth;

    const totalArea = elementHeight * elementWidth;

    return visibleArea > totalArea / 2;
  }

  // Handle the start of a touch event for swipe navigation
  handleTouchStart(event) {
    event.stopPropagation();
    this.touchStartX = event.touches[0].clientX;
    clearInterval(this.timer); // Stop the automatic slideshow
  }

  // Handle the movement during a touch event for swipe navigation
  handleTouchMove(event) {
    event.stopPropagation();
    if (!this.touchStartX) {
      return;
    }

    const touchEndX = event.touches[0].clientX;
    const diffX = this.touchStartX - touchEndX;

    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        this.currentIndex = (this.currentIndex + 1) % (this.totalSlides + 2);
      } else {
        this.currentIndex = (this.currentIndex - 1 + this.totalSlides + 2) % (this.totalSlides + 2);
      }

      this.touchStartX = null;
      this.scrollToSlide();

      this.initSlideShow(); // Restart the automatic slideshow
    }
  }

  handleResize(event) {
    this.container.scrollLeft = this.currentIndex * this.slides[0].clientWidth;
  }

  // Add event handlers for navigation buttons
  addNavigationEventHandlers() {
    this.buttonPrevious.addEventListener('click', () => {
      clearInterval(this.timer); // Stop the automatic slideshow

      this.currentIndex = (this.currentIndex - 1 + this.totalSlides + 2) % (this.totalSlides + 2);
      this.scrollToSlide();

      this.initSlideShow(); // Restart the automatic slideshow
    });

    this.buttonNext.addEventListener('click', () => {
      clearInterval(this.timer); // Stop the automatic slideshow

      this.currentIndex = (this.currentIndex + 1) % (this.totalSlides + 2);
      this.scrollToSlide();

      this.initSlideShow(); // Restart the automatic slideshow
    });
  }

  animateSlideTitle() {
    let currentIndexMod = (this.currentIndex - 1) % this.totalSlides;
    if (currentIndexMod < 0) {
      currentIndexMod = this.totalSlides - 1;
    } else if (currentIndexMod > this.totalSlides) {
      currentIndexMod = 0;
    }

    this.carouselTitle.classList.replace('opacity-100', 'opacity-0');
    const slide = this.slides[currentIndexMod];

    const carouselTitle = slide.dataset.carouselTitle;
    const carouselLink = slide.dataset.carouselLink;

    if (carouselTitle) {
      this.carouselTitle.classList.replace('opacity-100', 'opacity-0');

      setTimeout(() => {
        let newHTML;

        if (carouselLink) {
          newHTML = document.createElement('a');
          newHTML.href = carouselLink;
          newHTML.innerHTML = carouselTitle;
        } else {
          newHTML = document.createElement('span');
          newHTML.innerHTML = carouselTitle;
        }
        const ruler = this.querySelector('[data-carousel-ruler]');
        ruler.innerHTML = carouselTitle;
        requestAnimationFrame(() => {
          this.carouselTitle.style.width = `${ruler.clientWidth + 1}px`;
        });

        this.carouselTitle.innerHTML = '';
        this.carouselTitle.appendChild(newHTML);

        setTimeout(() => {
          this.carouselTitle.classList.replace('opacity-0', 'opacity-100');
        }, 300);
      }, 300);
    }
  }

  cloneFirstAndLastSlides() {
    const firstSlide = this.slides[0];
    const lastSlide = this.slides[this.totalSlides - 1];

    if (firstSlide) {
      const firstClone = firstSlide.cloneNode(true);
      this.container.appendChild(firstClone);
    }

    if (lastSlide) {
      const lastClone = lastSlide.cloneNode(true);
      this.container.insertBefore(lastClone, firstSlide);
    }

    requestAnimationFrame(() => {
      if (this.container.scrollLeft === 0) {
        this.container.scrollLeft = this.container.clientWidth;
      }
    });
  }

  removeLazyLoadFromNextSlide(nextIndex) {
    if (this.slides[nextIndex] && this.isElementInViewport(this)) {
      const image = this.slides[nextIndex].querySelector('img');
      if (image) {
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', 'high');
      }

      const video = this.slides[nextIndex].querySelector('video');
      if (video) {
        video.setAttribute('preload', 'auto');
        video.setAttribute('fetchpriority', 'high');
      }
    }
  }

  // handleScrollEnd = (scrollOptions) => {
  //   return new Promise((resolve) => {
  //     const checkScrollEnd = () => {
  //       if (Math.abs(this.container.scrollLeft - scrollOptions.left) < 1) {
  //         resolve();
  //       } else {
  //         requestAnimationFrame(checkScrollEnd);
  //       }
  //     };
  //     checkScrollEnd();
  //   });
  // };

  handleScrollEnd = (left) => {
    return new Promise((resolve) => {
      const checkScrollEnd = () => {
        if (Math.abs(this.container.scrollLeft - left) < 1) {
          resolve();
        } else {
          requestAnimationFrame(checkScrollEnd);
        }
      };
      checkScrollEnd();
    });
  };
}

customElements.define('index-carousel', IndexCarousel);
